import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/** Build filename: {tag}_Type-Number.pdf, or Type-Number.pdf if no tag */
export function makeDocFilename(tag, type, number) {
  const t = (tag || "").trim().replace(/\s+/g, "_");
  const n = (number || "doc").replace(/[^a-zA-Z0-9\-_]/g, "");
  return t ? `${t}_${type}-${n}.pdf` : `${type}-${n}.pdf`;
}

/**
 * @param {HTMLElement} node - element to capture
 * @param {object} opts
 * @param {boolean} opts.singlePage - if true, scales content to fit exactly 1 A4 page
 */
async function _renderToPdf(node, { singlePage = false } = {}) {
  const canvas = await html2canvas(node, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    windowWidth: 794,        // Force consistent A4-width rendering
    scrollX: 0,
    scrollY: 0,
  });
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  if (singlePage) {
    // Scale to fit on exactly 1 page — never cut content
    const scaledHeight = Math.min(imgHeight, pageHeight);
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, scaledHeight);
  } else {
    // Multi-page: tile the image across pages
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
  }
  return pdf;
}

/** Download PDF to disk
 * @param {HTMLElement} node
 * @param {string} filename
 * @param {object} [opts]
 * @param {boolean} [opts.singlePage] - scale to fit 1 page
 */
export async function downloadInvoicePdf(node, filename, { singlePage = false } = {}) {
  if (!node) return;
  const pdf = await _renderToPdf(node, { singlePage });
  pdf.save(filename);
}

/** Return base64 string (no extension, no data: prefix) for email attachment
 * @param {HTMLElement} node
 * @param {object} [opts]
 * @param {boolean} [opts.singlePage]
 */
export async function generatePdfBase64(node, { singlePage = false } = {}) {
  if (!node) return null;
  const pdf = await _renderToPdf(node, { singlePage });
  return pdf.output("datauristring").split(",")[1];
}
